import { useEffect, useRef } from "react";
import { useAlert } from "./AlertProvider";
import { useUserStore } from "../../Modules/Authentication/Store/userStore";
import { useGroupStore } from "../../Modules/Configurations/Categories/Store/useGroupStore";
import useAccountStore from "../../Modules/FinanceManager/Accounts/Store/useAccountStore";
import useTransactionStore from "../../Modules/FinanceManager/Transactions/Store/useTransactionStore";
import useGoalStore from "../../Modules/FinanceManager/SavingGoals/Store/useGoalStore";
import useFinancialPlanningStore from "../../Modules/FinanceManager/FinancialPlanning/Store/useFinancialPlanningStore";
import useReportingStore from "../../Modules/FinanceManager/Dashboard/Store/useReportingStore";
import useAssetStore from "../../Modules/HomeManager/Assets/Store/useAssetStore";
import useEventStore from "../../Modules/HomeManager/Events/Store/useEventStore";
import useMealPlanStore from "../../Modules/HomeManager/MealPlanning/Store/useMealPlanStore";
import useTaskStore from "../../Modules/HomeManager/TaskChores/Store/useTaskStore";

const INITIAL_VALUE = Symbol("initial-value");
const WRAPPED_KEY = "__wiseAlertWrapped";

const normalizeMessage = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return value.message?.trim() || "";
  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message.trim();
    if (typeof value.error === "string") return value.error.trim();
  }
  return "";
};

const useFeedbackNotification = (value, notify, title) => {
  const prevRef = useRef(INITIAL_VALUE);

  useEffect(() => {
    const message = normalizeMessage(value);

    if (prevRef.current === INITIAL_VALUE) {
      prevRef.current = message;
      return;
    }

    if (!message || message === prevRef.current) {
      prevRef.current = message;
      return;
    }

    prevRef.current = message;
    notify(message, { title });
  }, [notify, title, value]);
};

const actionAlerts = [
  {
    store: useAssetStore,
    actions: {
      createAsset: "Asset created successfully.",
      updateAsset: "Asset updated successfully.",
      deleteAsset: "Asset deleted successfully.",
    },
  },
  {
    store: useEventStore,
    actions: {
      createEvent: "Event saved successfully.",
      updateEvent: "Event updated successfully.",
      deleteEvent: "Event deleted successfully.",
    },
  },
  {
    store: useTaskStore,
    actions: {
      createTask: "Task created successfully.",
      updateTask: "Task updated successfully.",
      deleteTask: "Task deleted successfully.",
    },
  },
  {
    store: useMealPlanStore,
    actions: {
      createMealPlan: "Meal plan created successfully.",
      updateMealPlan: "Meal plan updated successfully.",
      deleteMealPlan: "Meal plan deleted successfully.",
      saveRecipe: "Recipe saved successfully.",
      updateRecipe: "Recipe updated successfully.",
      deleteRecipe: "Recipe deleted successfully.",
    },
  },
  {
    store: useTransactionStore,
    actions: {
      saveTransactionToFirestore: "Transaction saved successfully.",
      updateTransactionInFirestore: "Transaction updated successfully.",
      deleteTransaction: "Transaction deleted successfully.",
      // recalculateAllAccountBalancesFromFirestore intentionally NOT listed
      // here (sub-project 4b) — AccountPage now shows its own per-outcome
      // toast (success/throttled/error) for this action; a generic entry
      // here would double-toast on every reconcile.
    },
  },
  {
    store: useGoalStore,
    actions: {
      updateGoalStatus: "Goal status updated successfully.",
      deleteGoal: "Goal deleted successfully.",
    },
  },
  {
    store: useAccountStore,
    actions: {
      deleteAccount: "Account deleted successfully.",
    },
  },
  {
    store: useFinancialPlanningStore,
    actions: {
      savePlan: "Financial plan saved successfully.",
      deletePlan: "Financial plan deleted successfully.",
    },
  },
];

const wrapStoreActions = (store, actions, onSuccess, onError) => {
  const state = store.getState();
  const patches = {};

  Object.entries(actions).forEach(([actionName, successMessage]) => {
    const original = state[actionName];
    if (typeof original !== "function" || original?.[WRAPPED_KEY]) return;

    const wrappedAction = async (...args) => {
      const stateBeforeRun = store.getState();
      const hasErrorField = Object.prototype.hasOwnProperty.call(stateBeforeRun, "error");

      if (hasErrorField && stateBeforeRun.error) {
        store.setState({ error: null });
      }

      try {
        const result = await Promise.resolve(original(...args));
        const afterError = hasErrorField ? normalizeMessage(store.getState().error) : "";
        if (!afterError && successMessage) {
          onSuccess(successMessage);
        }
        return result;
      } catch (err) {
        const message = normalizeMessage(err) || "Something went wrong. Please try again.";
        onError(message, { title: "Action failed" });
        throw err;
      }
    };

    wrappedAction[WRAPPED_KEY] = true;
    patches[actionName] = wrappedAction;
  });

  if (Object.keys(patches).length > 0) {
    store.setState(patches);
  }
};

const GlobalAlertSync = () => {
  const { success, error } = useAlert();

  const userError = useUserStore((state) => state.error);
  const userSuccess = useUserStore((state) => state.successMessage);
  const groupError = useGroupStore((state) => state.error);
  const groupSuccess = useGroupStore((state) => state.success);
  const accountError = useAccountStore((state) => state.error);
  const transactionError = useTransactionStore((state) => state.error);
  const goalError = useGoalStore((state) => state.error);
  const planningError = useFinancialPlanningStore((state) => state.error);
  const reportingError = useReportingStore((state) => state.error);
  const assetError = useAssetStore((state) => state.error);
  const eventError = useEventStore((state) => state.error);
  const mealPlanError = useMealPlanStore((state) => state.error);
  const taskError = useTaskStore((state) => state.error);

  useFeedbackNotification(userError, error, "Authentication issue");
  useFeedbackNotification(userSuccess, success, "Success");
  useFeedbackNotification(groupError, error, "Unable to save changes");
  useFeedbackNotification(groupSuccess, success, "Success");
  useFeedbackNotification(accountError, error, "Account action failed");
  useFeedbackNotification(transactionError, error, "Transaction action failed");
  useFeedbackNotification(goalError, error, "Goal action failed");
  useFeedbackNotification(planningError, error, "Financial planning error");
  useFeedbackNotification(reportingError, error, "Dashboard load issue");
  useFeedbackNotification(assetError, error, "Asset action failed");
  useFeedbackNotification(eventError, error, "Event action failed");
  useFeedbackNotification(mealPlanError, error, "Meal planning action failed");
  useFeedbackNotification(taskError, error, "Task action failed");

  useEffect(() => {
    actionAlerts.forEach(({ store, actions }) => {
      wrapStoreActions(store, actions, success, error);
    });
  }, [error, success]);

  return null;
};

export default GlobalAlertSync;
